import { Flex, HStack, Square, Text, useDisclosure } from '@televet/kibble-ui';
import React, { ReactElement, useCallback, useRef, useState } from 'react';
import { ColorPicker, defaultColors } from 'shared/components/ColorPicker';
import { useOutsideClick } from 'shared/hooks/useOutsideClick';
import { ChannelStatusUpdateInput } from 'shared/types/graphql';

interface ChannelStatusColorPickerWrapperProps {
  channelStatusId: string;
  handleChannelStatusUpdate: (channelStatusId: string, data: ChannelStatusUpdateInput) => void;
  currentStatusColor: string;
  hasPermission?: boolean;
}

function ChannelStatusColorPickerWrapper({
  channelStatusId,
  handleChannelStatusUpdate,
  currentStatusColor,
  hasPermission,
}: ChannelStatusColorPickerWrapperProps): ReactElement {
  const wrapperRef = useRef(null);
  const [newStatusColor, setNewStatusColor] = useState(currentStatusColor);
  const { isOpen: isColorPickerOpen, onClose: onCloseColorPicker, onToggle: onToggleColorPicker } = useDisclosure();

  useOutsideClick(wrapperRef, () => onCloseColorPicker());

  const getColorName = (hex: string): string => {
    const color = defaultColors.find((color) => color.hex.toLowerCase() === hex.toLowerCase());
    return color?.name || 'Custom';
  };

  const closeColorPicker = useCallback(
    (color: string) => {
      onCloseColorPicker();
      handleChannelStatusUpdate(channelStatusId, { color: color });
    },
    [channelStatusId, handleChannelStatusUpdate, onCloseColorPicker],
  );

  return (
    <Flex
      className="StatusColorPicker__Container"
      align="center"
      flex="1"
      ref={wrapperRef}
      cursor={hasPermission ? 'pointer' : 'not-allowed'}
      pos="relative"
    >
      <HStack className="StatusColorPicker__CurrentStatus" spacing={2} onClick={onToggleColorPicker}>
        <Square className="StatusColorPicker__CurrentStatus--Color" size={4} borderRadius={1} bg={currentStatusColor} />
        <Text className="StatusColorPicker__CurrentStatus--ColorName" size="sm">
          {getColorName(currentStatusColor)}
        </Text>
      </HStack>

      {isColorPickerOpen && (
        <ColorPicker
          selectedColor={newStatusColor}
          onSelectColor={(color: string): void => setNewStatusColor(color)}
          closeColorPicker={closeColorPicker}
          containerStyles={{
            position: 'absolute',
            borderRadius: 2,
            backgroundColor: 'background.default',
            zIndex: 'overlay',
            boxShadow: 'md',
            left: 6,
          }}
        />
      )}
    </Flex>
  );
}

export default ChannelStatusColorPickerWrapper;
